// Lightweight analytics dispatcher used across the web app + Capacitor wrapper.
//
// Fires:
//   - first_open  : once per device (LocalStorage flag), distinguishes installs
//   - session     : every cold load (DAU/MAU)
//   - register    : after successful signup
//   - subscribe   : after successful checkout / VIP upgrade
//
// Privacy: the backend hashes the device_id before persisting it.

const API_URL = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : `${window.location.origin}/api`;

const DEVICE_KEY = 'smartkick_device_id';
const FIRST_OPEN_KEY = 'smartkick_first_open_v1';
const UTM_KEY = 'smartkick_utm_v1';
const FIRST_TOUCH_KEY = 'smartkick_first_touch_v1';

// Capture UTM params from the current URL (once, on first landing) and persist
// them so they attach to later events (register / start_trial / subscribe) even
// after the user navigates away from the tagged link.
function captureUtm() {
  try {
    if (typeof window === 'undefined') return {};
    const params = new URLSearchParams(window.location.search);
    const fresh = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'].forEach((k) => {
      const v = params.get(k);
      if (v) fresh[k] = v.slice(0, 64);
    });
    if (Object.keys(fresh).length) {
      // last-touch = most recent tagged visit (always overwrite)
      localStorage.setItem(UTM_KEY, JSON.stringify(fresh));
      // first-touch = the ORIGINAL discovery (write once, never overwrite)
      if (!localStorage.getItem(FIRST_TOUCH_KEY)) {
        localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify({ ...fresh, ts: new Date().toISOString() }));
      }
      return fresh;
    }
    const stored = localStorage.getItem(UTM_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (e) {
    return {};
  }
}

function _touch(rawKey) {
  try {
    const raw = localStorage.getItem(rawKey);
    if (!raw) return null;
    const u = JSON.parse(raw);
    const t = {};
    if (u.utm_source) t.source = u.utm_source;
    if (u.utm_medium) t.medium = u.utm_medium;
    if (u.utm_campaign) t.campaign = u.utm_campaign;
    if (u.utm_content) t.content = u.utm_content;
    if (u.ts) t.ts = u.ts;
    return Object.keys(t).length ? t : null;
  } catch (e) {
    return null;
  }
}

// First/last-touch attribution object for the signup payload.
export function getAttribution() {
  const first = _touch(FIRST_TOUCH_KEY);
  const last = _touch(UTM_KEY);
  const out = {};
  if (first) out.first_touch = first;
  if (last) out.last_touch = last;
  return Object.keys(out).length ? out : null;
}

function getUtm() {
  try {
    const stored = localStorage.getItem(UTM_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (e) {
    return {};
  }
}

// Which app this bundle is — the standalone Live Scores app or the main
// Predictions app. Set at build time (REACT_APP_VARIANT=scores) or via the
// preview override (?variant=scores). Lets the backend count installs per app.
function detectApp() {
  try {
    if (process.env.REACT_APP_VARIANT === 'scores') return 'scores';
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      if (params.get('variant') === 'scores') return 'scores';
    }
  } catch (e) { /* ignore */ }
  return 'predictions';
}

function getDeviceId() {
  try {
    let id = localStorage.getItem(DEVICE_KEY);
    if (!id) {
      id = `${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
      localStorage.setItem(DEVICE_KEY, id);
    }
    return id;
  } catch (e) {
    return `volatile-${Math.random().toString(36).slice(2, 14)}`;
  }
}

function detectPlatform() {
  // Capacitor injects window.Capacitor when wrapped as a native app.
  try {
    if (typeof window !== 'undefined' && window.Capacitor) {
      const native = window.Capacitor.getPlatform?.() || '';
      if (native === 'ios') return 'ios';
      if (native === 'android') return 'android';
    }
    if (typeof window !== 'undefined' && window.matchMedia?.('(display-mode: standalone)').matches) {
      return 'pwa';
    }
  } catch (e) { /* ignore */ }
  return 'web';
}

export async function trackEvent(event, extra = {}) {
  try {
    const payload = {
      event,
      platform: detectPlatform(),
      app: detectApp(),
      device_id: getDeviceId(),
      path: typeof window !== 'undefined' ? window.location.pathname : null,
      ...getUtm(),
      ...extra,
    };
    // Use sendBeacon when available so it survives page-unload (e.g. after register redirect).
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      navigator.sendBeacon(`${API_URL}/analytics/event`, blob);
      return;
    }
    await fetch(`${API_URL}/analytics/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch (e) {
    // Analytics must never break the app.
  }
}

let bootstrapped = false;
export function bootstrapAnalytics() {
  if (bootstrapped) return;
  bootstrapped = true;
  captureUtm();
  // Namespace the first-open flag per app so the Live Scores app and the main
  // Predictions app each record their own install even in the same browser.
  const variant = detectApp();
  const firstOpenKey = variant === 'scores' ? `${FIRST_OPEN_KEY}_scores` : FIRST_OPEN_KEY;
  try {
    const seen = localStorage.getItem(firstOpenKey);
    if (!seen) {
      localStorage.setItem(firstOpenKey, new Date().toISOString());
      trackEvent('first_open');
    } else {
      trackEvent('session');
    }
  } catch (e) {
    // Fall back to a session event if localStorage is blocked.
    trackEvent('session');
  }
}
